// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — area over a threshold, through time, in Analyze's Statistics tab
 * (research tier, statistics S7; the arithmetic is
 * `services/research/threshold.ts`).
 *
 * The reader picks above or below and a value (in the data's units, or,
 * with a baseline held, in its change or σ change), and the panel measures
 * the region's area over it on the frame on screen. On a stream with a time
 * axis of frames it then fills a series as playback passes the frames, the
 * way pins fill their graph: one point per frame, keyed by the frame, so a
 * replay overwrites; "N of M frames read" and *Read the missing frames*
 * (the stepping S2's period uses, `periodRun.stepFrames`). The graph is
 * drawn as the pins' graph is (same marks, the green playhead, the dashed
 * line under the pointer, a click plays from that frame).
 *
 * Gated as the other research tools (decision R2): open on Research and
 * Situation Room plans (or `xr-cap-research`); a lock that opens the plans
 * on plans with the data tools; nothing for a class borrowing them.
 *
 * The recording ends with the panel (closing it, a new dataset, "Clear
 * pins, lines and boxes"), and starts again when the threshold, the
 * comparison or the region changes.
 */

import type { AnalyzeSource, AnalyzeTimeAxis } from './analyzeUI'
import type { LumaSnapshot } from '../services/glLumaSampler'
import type { LatLonBounds, TexelWindow } from '../services/datasetStats'
import type { DatasetOverlayOptions } from '../types'
import type { DisplayColorScale } from '../types/unit-scale'
import { releaseOf } from '../types/color-scale'
import { buildSeriesChart } from '../services/measure/chartGeometry'
import { valueTable } from '../services/research/changeMap'
import { changeViewState, type BaselineField } from '../services/research/changeView'
import { gridKey } from '../services/research/baseline'
import { periodMethodText } from '../services/research/climatology'
import { quantisationStep } from '../services/research/hovmoller'
import { stepFrames } from '../services/research/periodRun'
import { trail, trailOn } from '../services/research/researchTrail'
import {
  THRESHOLD_METHOD,
  buildThresholdCsvText,
  cellRowAreasKm2,
  codeSteps,
  createThresholdSeries,
  describeRule,
  frameAtFraction,
  frameStampMs,
  measureThreshold,
  missingThresholdFrames,
  recordThresholdFrame,
  skippedThresholdFrames,
  thresholdChartSeries,
  type Comparator,
  type ThresholdFrame,
  type ThresholdMetric,
  type ThresholdMode,
  type ThresholdReference,
  type ThresholdResult,
  type ThresholdRule,
  type ThresholdSeries,
} from '../services/research/threshold'
import { analysisExportsLocked, dataToolsAllowed, dataToolsLent, researchToolsAllowed, researchUpsellShown } from '../services/planAccess'
import { formatStatValue, renderStatTile } from './analyzeCharts'
import { downloadCsv } from './analyzeExport'
import { escapeHtml } from './domUtils'
import { ayniCsvFilename } from './pinsCsv'
import { openPlans } from './xrPlansUI'
import { t } from '../i18n'
import { formatDate, formatNumber } from '../i18n/format'
import { frameIsoForFile, researchFrameAxis, researchPrecision } from '../services/research/frameTime'
import { extremesOf } from '../services/measure/whenExtremes'
import { fillWhenHost, renderWhenBlock } from './whenExtremesUI'

type AnalyzeFrame = { snapshot: LumaSnapshot; scale: DisplayColorScale; options: DatasetOverlayOptions }

/** What the Statistics tab hands the section: the frame and the region its numbers cover. */
export interface ThresholdContext {
  src: AnalyzeSource
  frame: AnalyzeFrame
  /** The region's cells (undefined: the whole dataset), as the statistics use them. */
  window: TexelWindow | undefined
  /** `baseline.regionKey`: equal keys, the same cells. */
  regionKey: string
  /** The region as the picker names it. */
  regionLabel: string
  /** The region in English, for the CSV and the methods paragraph (as the statistics name it). */
  regionEnglish: string
  regionBounds: LatLonBounds | null
  /** The region's 90th percentile on this frame, offered as a starting value. */
  p90: number
}

interface Recorder {
  readonly key: string
  readonly datasetId: string | null
  readonly gridKey: string
  readonly width: number
  readonly height: number
  readonly window: TexelWindow | undefined
  readonly rowAreas: Float64Array
  readonly values: Float64Array
  readonly steps: Float64Array
  readonly rule: ThresholdRule
  readonly scale: DisplayColorScale
  readonly regionLabel: string
  readonly regionEnglish: string
  readonly regionBounds: LatLonBounds | null
  /** The reference for a frame of the axis (null: the frame on the globe now). */
  readonly reference: (frame: number | null) => ThresholdReference | null
  readonly baselineCsv: { label: string; method?: string } | null
  /** What a change is measured from; null for values. */
  readonly against: 'frame' | 'period' | null
  readonly axis: AnalyzeTimeAxis | null
  readonly series: ThresholdSeries | null
  stop: (() => void) | null
  stepping: boolean
  cancel: boolean
}

interface View {
  host: HTMLElement
  status: HTMLElement | null
  plot: HTMLElement | null
  readout: HTMLElement | null
  stepBtn: HTMLButtonElement | null
  /** AYNI (owner, 2026-10-18): when the area was largest and smallest. */
  when?: HTMLElement | null
}

/** The comparison as typed: one threshold kept per mode, so switching back finds it. */
const thresholds: Record<ThresholdMode, number | null> = { value: null, difference: null, sigma: 2 }
let mode: ThresholdMode = 'value'
let comparator: Comparator = 'above'
let metric: ThresholdMetric = 'area'
let recorder: Recorder | null = null
let view: View | null = null
let current: ThresholdContext | null = null
/** The frame under the pointer or the keyboard cursor, and what a seek said. */
let cursor: number | null = null
let said: { text: string; at: number } | null = null
let drawQueued = false
const fieldIds = new WeakMap<BaselineField, number>()
let nextFieldId = 1

/** The graph's box, in its own units (the pins' graph in the panel, a little shorter). */
const G = { w: 390, h: 150, left: 40, right: 6, top: 10, plot: 108, railTop: 124, rail: 3 }
/** Unread frames a trace may cross, as on the pins' graph. */
const BRIDGE = 6

/** AYNI (the chatbot's `threshold.area`): what the section measured on the frame on screen when it last drew. */
export interface ThresholdMeasured {
  readonly result: ThresholdResult
  readonly rule: ThresholdRule
  readonly ruleText: string
  readonly units: string | null
  readonly region: string
  readonly frame: string | null
  /** σ was raised to one step of the data in some cells (σ change understates there). */
  readonly sigmaFloored: boolean
}
let measured: ThresholdMeasured | null = null
/** AYNI (L6): the chatbot set the comparison, so the series noted as it fills is the chatbot's, until the person changes it. */
let ruleByAssistant = false

/**
 * AYNI (the chatbot, 2026-10-07): set the comparison the section measures
 * (as if typed in it), for the next time it draws; Analyze's Statistics tab
 * then shows the same numbers the chatbot reports.
 */
export function setThresholdRule(rule: ThresholdRule): void {
  ruleByAssistant = true
  mode = rule.mode
  comparator = rule.comparator
  thresholds[rule.mode] = rule.threshold
  measured = null
}

/** AYNI: what the section measured when it last drew (null before, or once it stopped). */
export function thresholdMeasured(): ThresholdMeasured | null {
  return measured
}

/** Stop recording and forget the series: the panel closed, the dataset changed, or everything was cleared. */
export function resetThreshold(): void {
  measured = null
  recorder?.stop?.()
  if (recorder) recorder.cancel = true
  recorder = null
  view = null
  current = null
  cursor = null
  said = null
}

/**
 * What the methods paragraph may say about the area over a threshold
 * (`research/methods.ts`): only once a frame has been measured into the
 * series, and only what that series is.
 */
export function thresholdMethodsState(): {
  mode: ThresholdMode
  comparator: Comparator
  threshold: number
  units: string | null
  region: string
  framesRead: number
  frames: number | null
  skipped: number
  against: 'frame' | 'period' | null
} | null {
  const rec = recorder
  if (!rec || !researchToolsAllowed()) return null
  const read = rec.series?.read ?? 0
  if (rec.series && read === 0) return null
  return {
    mode: rec.rule.mode,
    comparator: rec.rule.comparator,
    threshold: rec.rule.threshold,
    units: rec.scale.units ?? null,
    region: rec.regionEnglish,
    framesRead: rec.series ? read : 1,
    frames: rec.series ? rec.series.total : null,
    skipped: rec.series ? skippedThresholdFrames(rec.series) : 0,
    against: rec.against,
  }
}

/**
 * AYNI: a research record, when one is recording, keeps the area over a
 * threshold (slice 4): its definition, the frame on screen and a summary of
 * the series read so far. One entry per definition, replaced as it fills.
 */
function trailThreshold(rec: Recorder, now: ThresholdResult | null): void {
  if (!trailOn()) return
  const s = rec.series
  let read = 0
  let min = Infinity
  let max = -Infinity
  let sum = 0
  if (s) {
    for (const r of s.results) {
      if (!r || r.cellsWithData === 0) continue
      read++
      min = Math.min(min, r.areaKm2)
      max = Math.max(max, r.areaKm2)
      sum += r.areaKm2
    }
  }
  const units = rec.scale.units ?? ''
  trail({
    type: 'threshold',
    ...(ruleByAssistant ? { actor: 'assistant' as const } : {}),
    coalesce: `threshold|${rec.key}`,
    data: {
      rule: describeRule(rec.rule, units),
      mode: rec.rule.mode,
      comparator: rec.rule.comparator,
      threshold: rec.rule.threshold,
      units: rec.rule.mode === 'sigma' ? 'sigma' : units,
      region: rec.regionEnglish,
      ...(rec.regionBounds ? { bounds: { ...rec.regionBounds } } : {}),
      ...(rec.baselineCsv ? { baseline: rec.baselineCsv.label, baselineMethod: rec.baselineCsv.method ?? '' } : {}),
      grid: `${rec.width} x ${rec.height}`,
      ...(now ? { frameAreaKm2: now.areaKm2, frameAreaLowKm2: now.areaLowKm2, frameAreaHighKm2: now.areaHighKm2, framePercent: now.percent, frameCellsOver: now.cellsOver, frameCellsWithData: now.cellsWithData } : {}),
      ...(s ? { framesRead: s.read, frames: s.total, framesNotMeasured: skippedThresholdFrames(s) } : {}),
      ...(read ? { seriesMinKm2: min, seriesMaxKm2: max, seriesMeanKm2: sum / read } : {}),
      method: THRESHOLD_METHOD,
      summary: `Area where ${describeRule(rec.rule, units)} over ${rec.regionEnglish}${now ? `: ${Math.round(now.areaKm2).toLocaleString('en-GB')} km² on this frame` : ''}${s ? `; ${s.read} of ${s.total} frames read` : ''}`, // i18n-exempt: record text, English like the CSV
    },
  })
}

/** For tests: the series as it stands. */
export function thresholdSeries(): ThresholdSeries | null {
  return recorder?.series ?? null
}

function para(cls: string, text: string): HTMLParagraphElement {
  const p = document.createElement('p')
  p.className = cls
  p.textContent = text
  return p
}

function button(text: string, cls: string, onClick: (b: HTMLButtonElement) => void): HTMLButtonElement {
  const b = document.createElement('button')
  b.type = 'button'
  b.className = cls
  b.textContent = text
  b.addEventListener('click', () => onClick(b))
  return b
}

/** A frame's date on the axis, as fine as the axis needs (UTC). */
function timeLabel(ms: number, spanMs: number): string {
  const day = 86_400_000
  const utc = { timeZone: 'UTC' } as const
  // AYNI: never finer than the frames (a yearly stream is labelled by its year).
  const precision = researchPrecision()
  if (precision === 'year') return formatDate(ms, { ...utc, year: 'numeric' })
  if (precision === 'month' || spanMs > 20 * 365 * day) return formatDate(ms, { ...utc, year: 'numeric', month: 'short' })
  if (spanMs <= 2 * day) return formatDate(ms, { ...utc, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  if (spanMs <= 400 * day) return formatDate(ms, { ...utc, month: 'short', day: 'numeric' })
  return formatDate(ms, { ...utc, year: 'numeric', month: 'short' })
}

function km2(value: number): string {
  return formatStatValue(value, t('analyze.units.km2'))
}

function percentText(value: number | null): string {
  return value === null ? t('analyze.stat.none') : t('research.thr.percent', { value: formatNumber(value, { maximumSignificantDigits: 3 }) })
}

/** The held baseline, when the frame on screen can be read against it: same units, same grid, values (not classes). */
function baselineFor(ctx: ThresholdContext): BaselineField | null {
  const s = changeViewState()
  if (!s.field || s.other) return null
  const f = ctx.frame
  if ((s.field.units ?? '') !== (f.scale.units ?? '')) return null
  if (s.field.gridKey !== gridKey(f.snapshot.width, f.snapshot.height, f.options)) return null
  if (!s.field.period && s.field.data.length !== f.snapshot.width * f.snapshot.height) return null
  if (releaseOf(s.field.scale)?.kind === 'luma8-classified') return null
  return s.field
}

function modesFor(field: BaselineField | null): ThresholdMode[] {
  if (!field) return ['value']
  return field.period?.clim.hasSigma ? ['value', 'difference', 'sigma'] : ['value', 'difference']
}

function fieldId(field: BaselineField | null): number {
  if (!field) return 0
  let id = fieldIds.get(field)
  if (!id) {
    id = nextFieldId++
    fieldIds.set(field, id)
  }
  return id
}

/**
 * The reference for each frame: the held frame, or the period's mean (and σ)
 * for that frame's group: its calendar month, or month and UTC hours, from
 * its index on the axis (`groupAt` maps the frame's stamp back to its index
 * and `climatology.frameOf`). A frame that cannot be placed in the
 * climatology's calendar is not compared.
 */
function referenceFor(field: BaselineField | null, axis: AnalyzeTimeAxis | null): (frame: number | null) => ThresholdReference | null {
  if (!field) return () => null
  const period = field.period
  if (period) {
    return (frame) => {
      const group = frame !== null && axis ? period.groupAt(frameStampMs(axis, frame)) : period.groupNow()
      return group === null ? null : { kind: 'climatology', clim: period.clim, group }
    }
  }
  const values = valueTable(field.scale)
  const ref: ThresholdReference = { kind: 'frame', codes: field.data, values, steps: codeSteps(values) }
  return () => ref
}

/** The period baseline's method, in English for the CSV: S2's own words, and the thinning when there is one. */
function periodBaselineCsv(field: BaselineField): string {
  const clim = field.period!.clim
  const thinned = clim.factor > 1
    ? `; the climatology keeps one texel in ${clim.factor} each way (memory), and each cell is compared at the texel its climatology cell was read at`
    : ''
  return `${periodMethodText(clim.calendar.grouping)}; each frame's group taken from its index on the time axis${thinned}` // i18n-exempt: CSV row, English like every CSV row
}

function frameInput(snapshot: LumaSnapshot, rec: Recorder): ThresholdFrame {
  return { codes: snapshot.data, width: snapshot.width, height: snapshot.height, values: rec.values, steps: rec.steps, rowAreas: rec.rowAreas, window: rec.window }
}

/** The recorder for this rule, region and frame grid: the same one if nothing changed, else a new, empty one. */
function ensureRecorder(ctx: ThresholdContext, rule: ThresholdRule, field: BaselineField | null): Recorder {
  const { snapshot, scale, options } = ctx.frame
  const grid = gridKey(snapshot.width, snapshot.height, options)
  const datasetId = ctx.src.datasetId()
  const key = [datasetId, grid, ctx.regionKey, rule.mode, rule.comparator, rule.threshold, fieldId(field)].join('|')
  if (recorder?.key === key) return recorder
  // A new definition: the old series stops (the context and the view stay).
  recorder?.stop?.()
  if (recorder) recorder.cancel = true
  recorder = null
  cursor = null
  const axis = ctx.src.timeAxis?.() ?? null
  const values = valueTable(scale)
  const baselineCsv = !field
    ? null
    : field.period
      ? { label: `${field.period.label} climatology`, method: periodBaselineCsv(field) }
      : { label: `frame held at ${field.frameLabel ?? 'a frame without a date'}`, method: 'value minus the held frame\'s value in the same cell' } // i18n-exempt: CSV row
  const rec: Recorder = {
    key,
    datasetId,
    gridKey: grid,
    width: snapshot.width,
    height: snapshot.height,
    window: ctx.window,
    rowAreas: cellRowAreasKm2(snapshot.width, snapshot.height, options),
    values,
    steps: codeSteps(values),
    rule,
    scale,
    regionLabel: ctx.regionLabel,
    regionEnglish: ctx.regionEnglish,
    regionBounds: ctx.regionBounds,
    reference: referenceFor(field, axis),
    baselineCsv,
    against: field ? (field.period ? 'period' : 'frame') : null,
    axis,
    series: axis ? createThresholdSeries(axis, axis.usable ? (i) => axis.usable!(i) : undefined) : null,
    stop: null,
    stepping: false,
    cancel: false,
  }
  recorder = rec
  if (axis && rec.series) rec.stop = axis.onFrame((i) => readFrame(rec, i))
  return rec
}

/** Read the frame on the globe into the series, if it is still this recorder's dataset and grid. */
function readFrame(rec: Recorder, frame: number): void {
  if (recorder !== rec || !rec.series) return
  // The section went (the region has no data now): stop with it.
  if (!view?.host.isConnected) {
    resetThreshold()
    return
  }
  const src = current?.src
  if (!src || src.datasetId() !== rec.datasetId) return
  const f = src.frame()
  if (!f || gridKey(f.snapshot.width, f.snapshot.height, f.options) !== rec.gridKey) return
  const result = measureThreshold(frameInput(f.snapshot, rec), rec.rule, rec.reference(frame))
  recordThresholdFrame(rec.series, frame, result)
  queueDraw()
}

function queueDraw(): void {
  if (drawQueued) return
  drawQueued = true
  const run = (): void => {
    drawQueued = false
    paintSeries()
  }
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run)
  else setTimeout(run, 16)
}

// --- the graph -----------------------------------------------------------------

function xOf(frac: number): number {
  return G.left + frac * (G.w - G.left - G.right)
}

function fracOfFrame(series: ThresholdSeries, frame: number): number {
  const last = series.results.length - 1
  return last > 0 ? frame / last : 0
}

function tick(value: number): string {
  // i18n-exempt: a number on the graph's axis; the units are drawn once above it
  return metric === 'percent' ? formatNumber(value, { maximumSignificantDigits: 3 }) : formatNumber(value, { notation: 'compact', maximumSignificantDigits: 3 })
}

function chartMarkup(rec: Recorder): string {
  const series = rec.series!
  const data = thresholdChartSeries(series, metric)
  if (!data) return ''
  // An area's natural origin is zero: an invisible anchor there keeps the axis from starting at the smallest area read.
  const anchor = { ...data, slots: data.slots.map((_, i) => (i === 0 ? { sampled: true, value: 0 } : { sampled: false, value: null })) }
  const chart = buildSeriesChart([{ id: 'area', color: 'currentColor', series: data }, { id: 'zero', color: 'none', series: anchor }], { bridge: BRIDGE })
  const plotW = G.w - G.left - G.right
  const bottom = G.top + G.plot
  const span = data.endMs - data.startMs
  const parts: string[] = []
  parts.push(`<line x1="${G.left}" y1="${G.top}" x2="${G.left}" y2="${bottom}" class="tools-data-axis"/>`)
  parts.push(`<line x1="${G.left}" y1="${bottom}" x2="${G.w - G.right}" y2="${bottom}" class="tools-data-axis"/>`)
  const units = metric === 'percent' ? t('research.thr.axisPercent') : t('analyze.units.km2')
  parts.push(`<text x="${G.left + 3}" y="${G.top + 4}" class="tools-data-tick tools-data-units">${escapeHtml(units)}</text>`)
  if (chart) {
    const tr = chart.traces[0]
    parts.push(`<text x="${G.left - 4}" y="${G.top + 4}" text-anchor="end" class="tools-data-tick">${escapeHtml(tick(chart.max))}</text>`)
    parts.push(`<text x="${G.left - 4}" y="${bottom}" text-anchor="end" class="tools-data-tick">${escapeHtml(tick(chart.min))}</text>`)
    for (const seg of tr.segments) {
      const d = seg.map((p, i) => `${i ? 'L' : 'M'}${(G.left + p.x * plotW).toFixed(1)},${(G.top + p.y * G.plot).toFixed(1)}`).join('')
      parts.push(`<path d="${d}" class="analyze-thr-trace"/>`)
    }
    for (const p of tr.dots) parts.push(`<circle cx="${(G.left + p.x * plotW).toFixed(1)}" cy="${(G.top + p.y * G.plot).toFixed(1)}" r="1.8" class="analyze-thr-dot"/>`)
    // The coverage rail: filled where a frame was read, faint where it had no data.
    const railW = plotW / chart.slots
    tr.rail.forEach((state, s) => {
      if (state === 'unsampled') return
      parts.push(`<rect x="${(G.left + s * railW).toFixed(1)}" y="${G.railTop}" width="${Math.max(0.6, railW).toFixed(2)}" height="${G.rail}" class="analyze-thr-rail${state === 'nodata' ? ' analyze-thr-rail-nodata' : ''}"/>`)
    })
  }
  const now = rec.axis?.currentFrame() ?? null
  if (now !== null) {
    const x = xOf(fracOfFrame(series, now)).toFixed(1)
    parts.push(`<line x1="${x}" y1="${G.top}" x2="${x}" y2="${bottom}" class="tools-data-playhead"/>`)
  }
  if (cursor !== null) {
    const x = xOf(fracOfFrame(series, cursor)).toFixed(1)
    parts.push(`<line x1="${x}" y1="${G.top}" x2="${x}" y2="${bottom}" class="tools-data-hover-line analyze-thr-cursor"/>`)
  }
  const labelY = G.railTop + G.rail + 10
  parts.push(`<text x="${G.left}" y="${labelY}" class="tools-data-tick">${escapeHtml(timeLabel(data.startMs, span))}</text>`)
  parts.push(`<text x="${G.w - G.right}" y="${labelY}" text-anchor="end" class="tools-data-tick">${escapeHtml(timeLabel(data.endMs, span))}</text>`)
  return `<svg class="tools-data-chart-svg analyze-thr-svg" viewBox="0 0 ${G.w} ${labelY + 3}" aria-hidden="true">${parts.join('')}</svg>`
}

function describeFrame(rec: Recorder, frame: number): string {
  const series = rec.series!
  const span = frameStampMs(series.axis, series.results.length - 1) - series.axis.startMs
  const time = timeLabel(frameStampMs(series.axis, frame), span)
  const r = series.results[frame]
  if (series.skip[frame]) return t('research.thr.readout.skipped', { time })
  if (!r) return t('research.thr.readout.unread', { time })
  if (r.cellsWithData === 0) return t('research.thr.readout.noData', { time })
  return t('research.thr.readout.cell', {
    time,
    area: km2(r.areaKm2),
    share: percentText(r.percent),
    over: formatNumber(r.cellsOver),
    cells: formatNumber(r.cellsWithData),
  })
}

function paintStatus(rec: Recorder): void {
  const v = view
  const series = rec.series
  if (!v?.status || !series) return
  const skipped = skippedThresholdFrames(series)
  v.status.textContent = t('research.thr.progress', { read: formatNumber(series.read), total: formatNumber(series.total) })
    + (skipped ? ` ${t('research.thr.skipped', { count: formatNumber(skipped) })}` : '')
  if (v.stepBtn) {
    // Stopped is shown at once, though the frame being shown may still arrive.
    const running = rec.stepping && !rec.cancel
    const missing = series.read < series.total
    v.stepBtn.hidden = !missing && !running
    v.stepBtn.textContent = running ? t('research.thr.stop') : t('research.thr.step')
    v.stepBtn.classList.toggle('analyze-thr-stop', running)
  }
}

function paintSeries(): void {
  const rec = recorder
  const v = view
  if (!rec?.series || !v?.plot || !v.host.isConnected) return
  v.plot.innerHTML = chartMarkup(rec)
  paintStatus(rec)
  paintWhen(rec)
  if (v.readout && cursor !== null) v.readout.textContent = describeFrame(rec, cursor)
}

/** A frame's value on the graph now: its area, or its share of the area with data; null without data. */
function metricValue(r: ThresholdResult | null): number | null {
  if (!r || r.cellsWithData === 0) return null
  return metric === 'percent' ? r.percent : r.areaKm2
}

/**
 * AYNI (owner, 2026-10-18): when the area over the threshold was largest
 * and smallest, over the frames read (`measure/whenExtremes.ts`), each date
 * a button that plays from that frame.
 */
function paintWhen(rec: Recorder): void {
  const v = view
  const series = rec.series
  if (!v?.when || !series) return
  const n = series.results.length
  const e = extremesOf(n, (i) => metricValue(series.results[i]))
  const sig = e ? [metric, e.max.index, e.max.value, e.max.ties.length, e.min.index, e.min.value, e.min.ties.length, series.read, series.total].join('|') : 'none'
  fillWhenHost(v.when, sig, () => {
    if (!e) return null
    const span = frameStampMs(series.axis, n - 1) - series.axis.startMs
    const text = (x: number): string => (metric === 'percent' ? percentText(x) : km2(x))
    const row = (kind: 'max' | 'min', label: string, at: { value: number; index: number; ties: readonly number[] }) => ({
      kind,
      label,
      value: text(at.value),
      time: timeLabel(frameStampMs(series.axis, at.index), span),
      ties: at.ties.length - 1,
      onSeek: rec.axis ? () => {
        showCursor(at.index)
        seekFrame(at.index)
      } : null,
    })
    return renderWhenBlock(
      t('research.thr.when.title'),
      [row('max', t('research.thr.when.largest'), e.max), row('min', t('research.thr.when.smallest'), e.min)],
      series.read < series.total ? t('tools.data.when.partial', { read: formatNumber(series.read), total: formatNumber(series.total) }) : null,
    )
  })
}

function frameAtPointer(e: MouseEvent): number | null {
  const rec = recorder
  const svg = view?.plot?.querySelector('svg')
  if (!rec?.series || !svg) return null
  const r = svg.getBoundingClientRect()
  if (!(r.width > 0)) return null
  const x = ((e.clientX - r.left) / r.width) * G.w
  const frac = (x - G.left) / (G.w - G.left - G.right)
  if (frac < -0.03 || frac > 1.03) return null
  return frameAtFraction(rec.series, frac)
}

function showCursor(frame: number | null): void {
  cursor = frame
  paintSeries()
  const rec = recorder
  if (view?.readout && rec?.series) view.readout.textContent = frame === null ? t('research.thr.readoutHint') : describeFrame(rec, frame)
}

function seekFrame(frame: number): void {
  const rec = recorder
  if (!rec?.series || !rec.axis) return
  const ms = frameStampMs(rec.series.axis, frame)
  // The pins' clock moves playback without pausing it (as a click on the pins' graph); else show the frame.
  const seek = current?.src.hovmoller?.()?.seek
  if (seek) seek(ms)
  else void rec.axis.showFrame(frame)
  const span = frameStampMs(rec.series.axis, rec.series.results.length - 1) - rec.series.axis.startMs
  const text = t('research.thr.seeked', { time: timeLabel(ms, span) })
  said = { text, at: Date.now() }
  if (view?.readout) view.readout.textContent = text
}

function onPlotKey(e: KeyboardEvent): void {
  const rec = recorder
  if (!rec?.series) return
  const last = rec.series.results.length - 1
  const at = cursor ?? Math.max(0, rec.series.lastFrame)
  const page = Math.max(1, Math.round(last / 10))
  let next: number | null = null
  switch (e.key) {
    case 'ArrowLeft': next = Math.max(0, at - 1); break
    case 'ArrowRight': next = Math.min(last, at + 1); break
    case 'PageUp': next = Math.max(0, at - page); break
    case 'PageDown': next = Math.min(last, at + page); break
    case 'Home': next = 0; break
    case 'End': next = last; break
    case 'Enter':
    case ' ':
      e.preventDefault()
      seekFrame(at)
      return
    default:
      return
  }
  e.preventDefault()
  showCursor(next)
}

async function stepMissing(rec: Recorder): Promise<void> {
  const axis = rec.axis
  if (!axis || !rec.series || rec.stepping) return
  rec.stepping = true
  rec.cancel = false
  paintStatus(rec)
  await stepFrames(
    missingThresholdFrames(rec.series),
    (frame) => axis.showFrame(frame),
    (frame) => {
      if (!rec.series?.results[frame]) readFrame(rec, frame)
    },
    () => rec.cancel || recorder !== rec,
  )
  rec.stepping = false
  if (recorder === rec) paintStatus(rec)
}

// --- the section ------------------------------------------------------------------

function head(): HTMLElement {
  const h = document.createElement('div')
  h.className = 'analyze-transect-head'
  const title = document.createElement('h3')
  title.textContent = t('research.thr.title')
  h.appendChild(title)
  return h
}

/** The rule's controls: what is compared (with a baseline), above or below, and the value. */
function controls(ctx: ThresholdContext, modes: readonly ThresholdMode[], rerender: (focus: string) => void): HTMLElement {
  const wrap = document.createElement('div')
  wrap.className = 'analyze-thr-rule'
  if (modes.length > 1) {
    const group = document.createElement('div')
    group.className = 'analyze-change-modes analyze-thr-modes'
    group.setAttribute('role', 'group')
    group.setAttribute('aria-label', t('research.thr.modeAria'))
    const label: Record<ThresholdMode, string> = {
      value: t('research.thr.mode.value'),
      difference: t('research.thr.mode.difference'),
      sigma: t('research.thr.mode.sigma'),
    }
    for (const m of modes) {
      const b = button(label[m], 'analyze-action analyze-change-mode analyze-thr-mode', () => {
        if (mode === m) return
        mode = m
        ruleByAssistant = false
        rerender(`.analyze-thr-mode[data-value="${m}"]`)
      })
      b.dataset.value = m
      b.setAttribute('aria-pressed', String(mode === m))
      group.appendChild(b)
    }
    wrap.appendChild(group)
  }
  const line = document.createElement('div')
  line.className = 'analyze-thr-line'
  const select = document.createElement('select')
  select.className = 'analyze-thr-comparator'
  select.setAttribute('aria-label', t('research.thr.comparatorAria'))
  for (const [value, text] of [['above', t('research.thr.above')], ['below', t('research.thr.below')]] as const) {
    const opt = document.createElement('option')
    opt.value = value
    opt.textContent = text
    select.appendChild(opt)
  }
  select.value = comparator
  select.addEventListener('change', () => {
    comparator = select.value === 'below' ? 'below' : 'above'
    ruleByAssistant = false
    rerender('.analyze-thr-comparator')
  })
  const units = mode === 'sigma' ? t('research.thr.sigmaUnits') : ctx.frame.scale.units ?? ''
  const input = document.createElement('input')
  input.type = 'number'
  input.step = 'any'
  input.inputMode = 'decimal'
  input.className = 'analyze-thr-value'
  input.setAttribute('aria-label', t('research.thr.valueAria', { units: units || '—' }))
  const typed = thresholds[mode]
  input.value = typed === null ? '' : String(typed)
  if (mode === 'value' && Number.isFinite(ctx.p90)) input.placeholder = t('research.thr.placeholder', { value: formatNumber(ctx.p90, { maximumSignificantDigits: 3, useGrouping: false }) })
  input.addEventListener('change', () => {
    const v = input.value.trim() === '' ? null : Number(input.value)
    const next = v !== null && Number.isFinite(v) ? v : null
    if (next === thresholds[mode]) return
    thresholds[mode] = next
    ruleByAssistant = false
    rerender('.analyze-thr-value')
  })
  const unitText = document.createElement('span')
  unitText.className = 'analyze-thr-units'
  unitText.textContent = units
  line.append(select, input, unitText)
  wrap.appendChild(line)
  return wrap
}

/**
 * Draw the section into `host` (the Statistics tab, under the statistics).
 * Called whenever the statistics are computed (a settled frame, another
 * region); the recording outlives redraws for as long as nothing it
 * depends on changed.
 */
export function renderThresholdSection(host: HTMLElement, ctx: ThresholdContext): void {
  host.replaceChildren()
  view = null
  measured = null
  if (!researchToolsAllowed()) {
    resetThreshold()
    // A class borrowing the data tools sees nothing; a plan with them sees the lock.
    if (!researchUpsellShown()) {
      host.hidden = true
      return
    }
    host.hidden = false
    const locked = button(t('research.thr.title'), 'analyze-action analyze-thr-open analyze-locked', (b) => openPlans(b, 'research', { notice: t('plans.locked.threshold') }))
    locked.setAttribute('aria-describedby', 'analyze-thr-lock')
    const why = para('analyze-lock-note', t('research.thr.locked'))
    why.id = 'analyze-thr-lock'
    host.append(locked, why)
    return
  }
  host.hidden = false
  host.appendChild(head())
  current = ctx
  const rerender = (focus: string): void => {
    if (!current) return
    renderThresholdSection(host, current)
    host.querySelector<HTMLElement>(focus)?.focus()
  }
  if (releaseOf(ctx.frame.scale)?.kind === 'luma8-classified') {
    resetThreshold()
    host.appendChild(para('analyze-message', t('research.thr.classified')))
    return
  }
  host.appendChild(para('analyze-coverage analyze-thr-lead', t('research.thr.lead')))
  const field = baselineFor(ctx)
  const modes = modesFor(field)
  if (!modes.includes(mode)) mode = 'value'
  host.appendChild(controls(ctx, modes, rerender))
  if (field && mode !== 'value') {
    const clim = field.period?.clim
    host.appendChild(para('analyze-coverage analyze-thr-baseline', field.period
      ? t(clim?.calendar.grouping === 'subdaily' ? 'research.thr.baselinePeriodHours' : clim?.calendar.grouping === 'annual' || clim?.calendar.grouping === 'window' ? 'research.thr.baselinePeriodAll' : 'research.thr.baselinePeriod', { span: field.period.label })
      : t('research.thr.baselineFrame', { frame: field.frameLabel ?? t('research.baseline.noFrame') })))
    if (clim && clim.factor > 1) host.appendChild(para('analyze-coverage analyze-thr-thinned', t('research.thr.thinned', { factor: String(clim.factor) })))
  }
  if (mode === 'sigma' && field?.period) {
    const grouping = field.period.clim.calendar.grouping
    const spread = grouping === 'daily' ? t('research.period.spreadDaily') : grouping === 'subdaily' ? t('research.period.spreadSubdaily') : t('research.period.spreadYearly')
    host.appendChild(para('analyze-coverage analyze-thr-sigma-note', t('research.thr.sigmaNote', { spread })))
  }
  const threshold = thresholds[mode]
  if (threshold === null) {
    recorder?.stop?.()
    recorder = null
    view = { host, status: null, plot: null, readout: null, stepBtn: null }
    host.appendChild(para('analyze-message analyze-thr-prompt', t('research.thr.prompt')))
    host.appendChild(limits(ctx))
    return
  }

  const rule: ThresholdRule = { mode, comparator, threshold }
  const rec = ensureRecorder(ctx, rule, mode === 'value' ? null : field)
  view = { host, status: null, plot: null, readout: null, stepBtn: null }
  // The frame on screen: measured now, and a point of the series.
  const nowFrame = rec.axis?.currentFrame() ?? null
  const result = measureThreshold(frameInput(ctx.frame.snapshot, rec), rule, rec.reference(nowFrame))
  if (rec.series && nowFrame !== null) recordThresholdFrame(rec.series, nowFrame, result)
  measured = {
    result,
    rule,
    ruleText: describeRule(rule, ctx.frame.scale.units),
    units: ctx.frame.scale.units ?? null,
    region: ctx.regionEnglish,
    frame: ctx.src.frameTime?.() ?? null,
    sigmaFloored: mode === 'sigma' && (field?.period?.clim.flooredCells ?? 0) > 0,
  }
  host.appendChild(tiles(result, mode))
  host.appendChild(rangeNote(result))
  trailThreshold(rec, result)
  if (rec.series && nowFrame !== null && rec.series.skip[nowFrame]) host.appendChild(para('analyze-coverage analyze-thr-filled', t('research.thr.filledNow')))
  const measuredAt = ctx.src.frameTime?.()
  if (measuredAt) host.appendChild(para('analyze-coverage', t('analyze.frameTime', { time: measuredAt })))

  if (rec.series) renderSeries(host, rec)
  else host.appendChild(para('analyze-coverage analyze-thr-no-axis', t('research.thr.noAxis')))
  host.appendChild(limits(ctx))
}

function tiles(r: ThresholdResult, m: ThresholdMode): HTMLElement {
  const grid = document.createElement('div')
  grid.className = 'analyze-stats analyze-thr-stats'
  grid.appendChild(renderStatTile(t('research.thr.stat.area'), r.cellsWithData ? km2(r.areaKm2) : t('analyze.stat.none')))
  grid.appendChild(renderStatTile(t('research.thr.stat.share'), percentText(r.percent)))
  grid.appendChild(renderStatTile(t('research.thr.stat.cells'), t('research.thr.cellsOf', { over: formatNumber(r.cellsOver), total: formatNumber(r.cellsWithData) })))
  grid.appendChild(renderStatTile(m === 'value' ? t('research.thr.stat.noData') : t('research.thr.stat.noCompare'), formatNumber(r.cellsNoData)))
  return grid
}

function rangeNote(r: ThresholdResult): HTMLElement {
  if (r.cellsWithData === 0) return para('analyze-coverage analyze-thr-range', t(mode === 'value' ? 'research.thr.noData' : 'research.thr.noCompare'))
  return para('analyze-coverage analyze-thr-range', r.cellsAmbiguous > 0
    ? t('research.thr.range', { low: km2(r.areaLowKm2), high: km2(r.areaHighKm2), cells: formatNumber(r.cellsAmbiguous) })
    : t('research.thr.rangeNone'))
}

function limits(ctx: ThresholdContext): HTMLElement {
  const { snapshot, scale } = ctx.frame
  const step = quantisationStep(scale)
  const grid = { width: formatNumber(snapshot.width), height: formatNumber(snapshot.height) }
  return para('analyze-coverage analyze-thr-limits', step !== null
    ? t('research.thr.limits', { ...grid, step: formatStatValue(step, scale.units) })
    : t('research.thr.limitsUneven', grid))
}

function renderSeries(host: HTMLElement, rec: Recorder): void {
  const v = view!
  const sub = document.createElement('h4')
  sub.className = 'analyze-thr-subhead'
  sub.textContent = t('research.thr.seriesTitle')
  host.appendChild(sub)
  const status = para('analyze-coverage analyze-thr-status', '')
  status.setAttribute('aria-live', 'polite')
  host.appendChild(status)
  v.status = status

  // km² or the share of the area with data.
  const group = document.createElement('div')
  group.className = 'analyze-change-modes analyze-thr-metrics'
  group.setAttribute('role', 'group')
  group.setAttribute('aria-label', t('research.thr.metricAria'))
  for (const [m, text] of [['area', t('research.thr.metric.area')], ['percent', t('research.thr.metric.percent')]] as const) {
    const b = button(text, 'analyze-action analyze-change-mode analyze-thr-metric', () => {
      if (metric === m) return
      metric = m
      group.querySelectorAll<HTMLElement>('.analyze-thr-metric').forEach((el) => el.setAttribute('aria-pressed', String(el.dataset.value === m)))
      paintSeries()
    })
    b.dataset.value = m
    b.setAttribute('aria-pressed', String(metric === m))
    group.appendChild(b)
  }
  host.appendChild(group)

  const plot = document.createElement('div')
  plot.className = 'tools-data-chart analyze-thr-chart'
  plot.tabIndex = 0
  plot.setAttribute('role', 'group')
  plot.setAttribute('aria-roledescription', t('research.thr.roledescription'))
  plot.setAttribute('aria-label', t('research.thr.chartAria'))
  plot.setAttribute('aria-describedby', 'analyze-thr-readout')
  plot.title = t('research.thr.readoutHint')
  host.appendChild(plot)
  v.plot = plot
  const readout = para('analyze-hov-readout analyze-thr-readout', t('research.thr.readoutHint'))
  readout.id = 'analyze-thr-readout'
  readout.setAttribute('role', 'status')
  host.appendChild(readout)
  v.readout = readout
  const when = document.createElement('div')
  when.className = 'analyze-thr-when'
  when.hidden = true
  host.appendChild(when)
  v.when = when

  plot.addEventListener('pointermove', (e) => {
    const f = frameAtPointer(e)
    if (f !== null) showCursor(f)
  })
  plot.addEventListener('pointerleave', () => {
    if (document.activeElement !== plot) showCursor(null)
  })
  plot.addEventListener('click', (e) => {
    const f = frameAtPointer(e)
    if (f === null) return
    showCursor(f)
    seekFrame(f)
  })
  plot.addEventListener('keydown', onPlotKey)

  const actions = document.createElement('div')
  actions.className = 'analyze-thr-actions'
  const stepBtn = button(t('research.thr.step'), 'analyze-action analyze-thr-step', () => {
    if (rec.stepping) {
      // Stop, or carry on if a stop is still finishing the frame in hand.
      rec.cancel = !rec.cancel
      paintStatus(rec)
    } else void stepMissing(rec)
  })
  v.stepBtn = stepBtn
  actions.appendChild(stepBtn)
  actions.appendChild(button(t('research.thr.restart'), 'analyze-action analyze-thr-restart', () => {
    if (!rec.series) return
    rec.cancel = true
    rec.series.results.fill(null)
    rec.series.read = 0
    rec.series.lastFrame = -1
    cursor = null
    paintSeries()
  }))
  host.appendChild(actions)
  host.appendChild(para('analyze-coverage analyze-thr-resets', t('research.thr.resets')))
  if (!analysisExportsLocked()) {
    const csv = button(t('research.thr.csv'), 'analyze-export analyze-thr-csv', () => {
      const part = thresholdCsv()
      if (part) downloadCsv(part.name, part.text)
    })
    host.appendChild(csv)
  }
  paintSeries()
  if (said && Date.now() - said.at < 4000) readout.textContent = said.text
  else if (cursor !== null) readout.textContent = describeFrame(rec, cursor)
}

/** The series as the button saves it (and the record notes it); null without a series. */
function thresholdCsv(): { name: string; text: string } | null {
  const r = recorder
  const src = current?.src
  if (!r?.series || !src) return null
  trailThreshold(r, null)
  const text = buildThresholdCsvText(r.series, {
    datasetTitle: src.datasetTitle(),
    // AYNI: each frame at the axis's precision, as the pins' CSV (frameCalendar.ts).
    formatTime: (ms) => (researchFrameAxis() ? frameIsoForFile(ms) : null),
    rule: r.rule,
    units: r.scale.units,
    sourceUnits: r.scale.sourceUnits,
    region: r.regionEnglish,
    regionBounds: r.regionBounds,
    gridWidth: r.width,
    gridHeight: r.height,
    step: quantisationStep(r.scale),
    baseline: r.baselineCsv,
    // The series spans many frames: the view's own frame time is not its time.
    provenance: (src.spanProvenance?.() ?? src.provenance?.() ?? []).filter((row) => row[0] !== 'frame_time'),
  })
  return { name: ayniCsvFilename('threshold', src.datasetTitle(), new Date(), r.regionLabel), text }
}

/**
 * AYNI (S8): the area over a threshold as a CSV for the reproducibility
 * bundle, when a series is being recorded; null otherwise or on a plan
 * whose Analyze exports are locked.
 */
export function thresholdBundleCsv(): string | null {
  if (analysisExportsLocked()) return null
  return thresholdCsv()?.text ?? null
}

